import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { authErrorMessage } from '../auth/auth-errors';
import { useAuth } from '../auth/useAuth';
import { Brand } from '../components/Brand';

export function LoginPage() {
  const {
    user,
    signInWithJustbit,
    signIn,
    resetPassword,
    enterDemo,
    demoAvailable,
    configurationReady,
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState<'justbit' | 'password' | null>(null);
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { from?: string } | null)?.from || '/app';

  if (user) return <Navigate to={destination} replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setSubmitting('password');
    try {
      await signIn(email.trim(), password);
      navigate(destination, { replace: true });
    } catch (error) {
      setMessage({ tone: 'error', text: authErrorMessage(error) });
    } finally {
      setSubmitting(null);
    }
  };

  const handleJustbitSignIn = async () => {
    setMessage(null);
    setSubmitting('justbit');
    try {
      await signInWithJustbit();
      navigate(destination, { replace: true });
    } catch (error) {
      setMessage({ tone: 'error', text: authErrorMessage(error) });
    } finally {
      setSubmitting(null);
    }
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setMessage({ tone: 'error', text: 'Inserisci prima il tuo indirizzo email.' });
      return;
    }
    try {
      await resetPassword(email.trim());
      setMessage({ tone: 'success', text: 'Controlla la posta: abbiamo inviato le istruzioni per reimpostare la password.' });
    } catch (error) {
      setMessage({ tone: 'error', text: authErrorMessage(error) });
    }
  };

  const handleDemo = () => {
    enterDemo();
    navigate('/app', { replace: true });
  };

  return (
    <main className="login-page">
      <section className="login-story" aria-label="Presentazione Talos">
        <div className="login-story-grid" aria-hidden="true" />
        <div className="login-orbit orbit-one" aria-hidden="true" />
        <div className="login-orbit orbit-two" aria-hidden="true" />
        <Brand inverse />

        <div className="login-story-copy">
          <p className="section-kicker"><span /> Report automation</p>
          <h1>Dagli export al report.<br /><em>Senza passaggi manuali.</em></h1>
          <p>Salesforce, storico e regole di business diventano tabelle, confronti e report pronti per la verifica.</p>
          <div className="story-proof">
            <span><ShieldCheck size={16} /> Processo supervisionato</span>
            <span><span className="live-dot" /> Report ripetibili</span>
          </div>
        </div>

        <p className="login-legal">Piattaforma interna Justbit · Accesso consentito esclusivamente agli utenti autorizzati.</p>
      </section>

      <section className="login-access">
        <div className="mobile-login-brand"><Brand /></div>
        <form className="login-form" onSubmit={handleSubmit}>
          <span className="login-lock"><LockKeyhole size={22} /></span>
          <p className="eyebrow"><span /> Area riservata</p>
          <h2>Bentornato.</h2>
          <p className="login-intro">Accedi a Talos, la piattaforma di automazione della reportistica CRM.</p>

          {message && (
            <div className={`form-alert ${message.tone}`} role={message.tone === 'error' ? 'alert' : 'status'}>
              <AlertCircle size={17} />
              <span>{message.text}</span>
            </div>
          )}

          {!configurationReady && !demoAvailable && (
            <div className="form-alert error" role="alert">
              <AlertCircle size={17} />
              <span>La configurazione Firebase non è disponibile.</span>
            </div>
          )}

          <button
            className="justbit-sso-button"
            type="button"
            onClick={() => void handleJustbitSignIn()}
            disabled={submitting !== null || !configurationReady}
          >
            {submitting === 'justbit' ? (
              <><LoaderCircle className="spin" size={17} /> Accesso in corso…</>
            ) : (
              <><span>Accedi con</span><img src="/justbit-logo-white.svg" alt="Justbit" /></>
            )}
          </button>

          <div className="login-divider"><span>oppure</span></div>

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="nome.cognome@justbit.it"
              autoComplete="username"
              required
              autoFocus
            />
          </label>

          <label className="field">
            <span>Password</span>
            <span className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Inserisci la password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>

          <button className="reset-button" type="button" onClick={() => void handleReset()}>
            Password dimenticata?
          </button>

          <button className="button button-primary login-submit" type="submit" disabled={submitting !== null || !configurationReady}>
            {submitting === 'password' ? <><LoaderCircle className="spin" size={17} /> Accesso in corso…</> : <>Accedi al workspace <ArrowRight size={17} /></>}
          </button>

          {demoAvailable && (
            <>
              <div className="login-divider"><span>ambiente di sviluppo</span></div>
              <button className="button button-secondary login-submit" type="button" onClick={handleDemo}>
                <Sparkles size={16} /> Apri anteprima locale
              </button>
            </>
          )}

          <p className="login-note">Non è prevista la registrazione pubblica. Gli account sono gestiti da un Superadmin.</p>
        </form>
        <p className="login-footer">© {new Date().getFullYear()} Justbit · Talos</p>
      </section>
    </main>
  );
}
